<template>
  <el-card shadow="never" v-loading="loading">
    <el-card shadow="never" v-if="!showSummary">
      <!-- Current License Overview -->
      <el-card class="current-license-card">
        <template slot="header">
          <div class="card-header-custom">
            <i class="el-icon-document"></i>
            <span>Current License Overview</span>
          </div>
        </template>
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="info-item">
              <label>Start Date:</label>
              <span>{{ lease.start | formatDate }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>Units:</label>
              <span>{{ lease.units.map(unit => unit.name).join(', ') }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <label>Monthly Rent:</label>
              <span>{{ computeTotalCurrentPrice() | currencySign($root.state.facilityData) }}</span>
            </div>
          </el-col>
        </el-row>
      </el-card>

      <!-- Transfer Stages -->
      <div class="stages-container">
        <el-card
          class="stage-card"
          :key="stage.step"
          v-for="(stage, index) of stages"
        >
          <template slot="header">
            <div class="stage-header">
              <div class="stage-title">
                <i class="el-icon-s-flag"></i>
                <span>Transfer Stage {{ stageLabelForm(index + 1) }}</span>
                <el-tag v-if="index === 0" type="warning" size="small">Primary Transfer</el-tag>
              </div>
              <el-button
                v-if="stages.length > 1 && index === stages.length - 1"
                type="text"
                icon="el-icon-delete"
                @click="removeStage(index)"
                class="remove-stage-btn">
                Remove
              </el-button>
            </div>
          </template>

          <el-alert
            v-if="index === 0"
            type="info"
            :closable="false"
            show-icon
            class="stage-info-alert">
            <template slot="title">Important Information</template>
            The current license will automatically end one day before this transfer date. The new license configuration will begin on the selected date.
          </el-alert>

          <el-form :model="stages[index]" label-position="top" :disabled="stages[index].disabled">
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item
                  :label="index === 0 ? 'Date of Transfer' : 'Stage Start Date'"
                  :prop="'stages.' + index + '.start'"
                  :rules="[{ required: true, message: 'Please select a date' }]"
                >
                  <el-date-picker
                    v-model="stages[index].start"
                    :disabled="index !== 0"
                    @change="(date) => setEndLeaseDate(date, index)"
                    type="date"
                    :picker-options="index === 0 ? transferDatePickerOptions : datePickerOptions"
                    placeholder="Select date"
                    @focus="() => currentStep = index"
                    style="width: 100%">
                  </el-date-picker>
                  <span class="form-help-text" v-if="index === 0 && stages[index].start">
                    Current license will end on: {{ leaseEndDate | formatDate }}
                  </span>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item
                  label="Next Transfer Date (Optional)"
                  prop="end"
                  v-if="index < 1"
                >
                  <el-date-picker
                    v-model="stages[index].end"
                    type="date"
                    :picker-options="moveOutDatePickerOptions"
                    placeholder="Select date"
                    :disabled="!stages[index].start"
                    @focus="() => currentStep = index"
                    style="width: 100%">
                  </el-date-picker>
                  <span v-if="index === 0" class="form-help-text">Set this to create another transfer stage</span>
                </el-form-item>
              </el-col>
            </el-row>

            <el-form-item
              :label="labelForSelect(index)"
              :prop="'stages.' + index + '.units'"
              :rules="[{ required: true, message: 'Please select at least one unit' }]"
              v-if="stages[index].start"
            >
              <el-select
                v-model="stages[index].units"
                multiple
                @change="onUnitSelected"
                placeholder="Choose units to include in this stage"
                style="width: 100%"
                :loading="loadingUnits">
                <el-option-group v-for="group in unitOptions(index)" :key="group.label" :label="group.label">
                  <el-option
                    v-for="option in group.options"
                    :key="option.value"
                    :value="option.value"
                    :label="option.label"
                    :disabled="option.disabled">
                    <div class="unit-option">
                      <span class="unit-name">{{ option.unitName }}</span>
                      <span class="unit-details">{{ option.size }} sq ft • Tier {{ option.tier | tierFilter }}</span>
                      <span class="unit-price">{{ option.price | currencySign($root.state.facilityData) }}/mo</span>
                    </div>
                  </el-option>
                </el-option-group>
              </el-select>
              <span class="form-help-text">Units from the current license will maintain their pricing if selected above</span>
            </el-form-item>
          </el-form>

          <!-- Pricing Table - Auto-show when units selected -->
          <div v-if="stage.selectedUnits.length > 0" class="pricing-section">
            <h4 class="pricing-title">
              <i class="el-icon-money"></i>
              Pricing Details
            </h4>
            <el-table :data="stage.selectedUnits" class="pricing-table">
              <el-table-column label="Unit Name" width="120">
                <template slot-scope="scope">
                  {{ scope.row.name }}
                </template>
              </el-table-column>
              <el-table-column label="Size" width="100">
                <template slot-scope="scope">
                  {{ scope.row.computedSize }} sq ft
                </template>
              </el-table-column>
              <el-table-column prop="tierQuality" label="Tier" :width="80">
                <template slot-scope="scope">
                  <el-tag size="small" :type="getTierTagType(scope.row.tierQuality)">
                    {{ scope.row.tierQuality === 'AA' ? 'A+' : scope.row.tierQuality }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Monthly Rate" :width="200">
                <template slot-scope="scope">
                  <div class="price-cell">
                    <el-input-number
                      v-if="scope.row.editable && !scope.row.disabledEdit"
                      size="small"
                      :min="1"
                      v-model.number="scope.row.editPrice"
                      :controls="false"
                      :precision="2"
                      :step="1"
                      :disabled="stage.disabled"
                    >
                    </el-input-number>
                    <div v-else class="price-display">
                      {{ computePriceForStep(scope.row, false, true, stage) | currencySign($root.state.facilityData) }}
                    </div>
                    <el-button
                      v-if="!scope.row.disabledEdit && allowAccess('level_3')"
                      :type="scope.row.editable ? 'danger' : ''"
                      size="mini"
                      @click="scope.row.editable = !scope.row.editable"
                      :disabled="stage.disabled"
                      :icon="scope.row.editable ? 'el-icon-close' : 'el-icon-edit'">
                    </el-button>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="Security Deposit" :width="200">
                <template slot-scope="scope">
                  <div class="price-cell">
                    <el-input-number
                      v-if="scope.row.depositEditable && !scope.row.disabledEdit"
                      size="small"
                      :min="1"
                      v-model.number="scope.row.editDeposit"
                      :controls="false"
                      :precision="2"
                      :step="1"
                      :disabled="stage.disabled"
                    >
                    </el-input-number>
                    <div v-else class="price-display">
                      {{ computeSecurityDeposit(scope.row, false, true, stage) | currencySign($root.state.facilityData) }}
                    </div>
                    <el-button
                      v-if="!scope.row.disabledEdit && allowAccess('level_3')"
                      :type="scope.row.depositEditable ? 'danger' : ''"
                      size="mini"
                      :disabled="stage.disabled"
                      @click="scope.row.depositEditable = !scope.row.depositEditable"
                      :icon="scope.row.depositEditable ? 'el-icon-close' : 'el-icon-edit'">
                    </el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-card>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <el-button
          v-if="!stages.length || (stages[stages.length - 1] && stages[stages.length - 1].end)"
          icon="el-icon-plus"
          :disabled="stages.length === 3"
          @click="addNewStageFromParent">
          Add Another Stage
        </el-button>
        <el-button
          type="primary"
          v-if="stages.length && !showSummary"
          @click="() => prepareSelectedUnit(true)"
          icon="el-icon-view"
          :disabled="!canProceedToReview()">
          Review Transfer
        </el-button>
      </div>
    </el-card>
    <el-card v-if="showSummary" shadow="never" class="summary-card">
      <div class="summary-header">
        <h2 class="summary-title">
          <i class="el-icon-check-circle"></i>
          Review Transfer Summary
        </h2>
        <p class="summary-subtitle">Please review the transfer details before submitting</p>
      </div>

      <div class="timeline-summary">
        <div v-for="(stage, index) in summaryData" :key="stage.step" class="summary-stage">
          <div class="stage-summary-header">
            <el-tag :type="index === 0 ? 'info' : 'primary'" size="medium">
              {{ `Transfer Stage ${stageLabel(index + 1)}` }}
            </el-tag>
            <span class="stage-dates">
              {{ stage.start | formatDate }}
              <i class="el-icon-arrow-right" v-if="stage.end"></i>
              {{ stage.end | formatDate }}
            </span>
          </div>

          <el-table :data="stage.selectedUnits" class="summary-table" size="small">
            <el-table-column label="Unit" width="100">
              <template slot-scope="scope">
                {{ scope.row.name }}
              </template>
            </el-table-column>
            <el-table-column label="Size" width="80">
              <template slot-scope="scope">
                {{ scope.row.computedSize }} sq ft
              </template>
            </el-table-column>
            <el-table-column label="Tier" width="60">
              <template slot-scope="scope">
                {{ scope.row.tierQuality === 'AA' ? 'A+' : scope.row.tierQuality }}
              </template>
            </el-table-column>
            <el-table-column label="Monthly Rate" width="120">
              <template slot-scope="scope">
                <div class="summary-price">
                  <span v-if="index !== 0">
                    {{ scope.row.editable ? scope.row.editPrice : computePriceForStep(scope.row, false, true, stage) | currencySign($root.state.facilityData) }}
                  </span>
                  <span v-else>
                    {{ scope.row.updatedPrice ? scope.row.updatedPrice : computePriceForStep(scope.row, false, true, stage, true) | currencySign($root.state.facilityData) }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Security Deposit" width="120">
              <template slot-scope="scope">
                <div class="summary-price">
                  <span v-if="index !== 0">
                    {{ scope.row.depositEditable ? scope.row.editDeposit : computeSecurityDeposit(scope.row, false, true, stage) | currencySign($root.state.facilityData) }}
                  </span>
                  <span v-else>
                    {{ computeSecurityDeposit(scope.row, false, true, stage, true) | currencySign($root.state.facilityData) }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="facilitySecurityFeeLabel" width="120">
              <template slot-scope="scope">
                {{ computeSecurityFee(scope.row.computedSize) | currencySign($root.state.facilityData) }}
              </template>
            </el-table-column>
          </el-table>

          <div class="stage-summary-total">
            <span>Monthly Total:</span>
            <strong>{{ calculateStageTotal(stage) | currencySign($root.state.facilityData) }}/month</strong>
          </div>
        </div>
      </div>

      <el-divider></el-divider>

      <div class="agreement-section">
        <h4>License Agreement Options</h4>
        <el-alert
          v-if="!hasACustomAgreement && !hasNoAgreement"
          type="info"
          :closable="false"
          show-icon>
          <template slot="title">Automatic Agreement Generation</template>
          New license agreement(s) will be automatically sent to the member for electronic signature.
        </el-alert>
        <el-alert
          v-else-if="hasACustomAgreement && !hasNoAgreement"
          type="warning"
          :closable="false"
          effect="dark"
          show-icon>
          <template slot="title">Custom Agreement Detected</template>
          Cannot send new license agreement(s) automatically. Member has a custom agreement on file.
        </el-alert>
        <el-alert
          v-else
          type="error"
          :closable="false"
          effect="dark"
        >
          <template slot="title">No Agreement Detected</template>
          Cannot send new license agreement(s) automatically. Member has no agreement on file.
        </el-alert>

        <el-switch
          v-if="!hasACustomAgreement && !hasNoAgreement"
          v-model="sendAgreement"
          active-text="Send agreement(s) automatically"
          inactive-text="Do not send agreement(s)"
          class="agreement-switch">
        </el-switch>
      </div>

      <el-divider></el-divider>

      <div class="summary-actions">
        <el-button @click="() => showSummary = false" icon="el-icon-arrow-left">
          Back to Edit
        </el-button>
        <el-popconfirm
          title="Are you sure you want to submit this transfer?"
          @confirm="prepareLineItems"
          confirm-button-text="Yes, Submit"
          cancel-button-text="Cancel">
          <el-button
            slot="reference"
            type="primary"
            :loading="loading"
            icon="el-icon-check">
            Submit Transfer
          </el-button>
        </el-popconfirm>
      </div>
    </el-card>
  </el-card>
</template>

<script>
import axios from 'axios';
import { nanoid } from 'nanoid';
import { addDays } from 'date-fns';
import { allowAccess } from '../utils/roles';
import { UNIT_RENTAL_API_BASE, UNITS_API_BASE_V2, ADMIN_API_BASE, LEASE_TRANSFER_API, RS_ANVIL_API, ANVIL_TEMPLATE_ID2, CUSTOMER_API_BASE } from '../env';
import { getTotalMonths, setMoveinToUTC, setMoveoutToUTC } from '../utils/timing';
import { computeUnitPricing, round, getLeaseTaxLineItem } from '../utils/pricing';
import getParsedTokenTitle from '../utils/keycloak';
import { stillfullApplicableStatuses } from '../utils/status';

export default {
  name: 'UnitLeaseTransfer',
  props: {
    facility: { type: String, required: true },
    customer: { type: String, required: true },
    taxExempted: { type: Boolean, default: false },
    lease: { type: Object, required: true },
  },
  async mounted() {
    this.loading = true;
    await this.getFacility();
    this.getUnitRentals();
    this.addNewStageFromParent();
    this.getCustomer();
    this.loading = false;
  },
  computed: {
    securityFeeEnabled() {
      return this.facilityData
        && this.facilityData.securityFee
        && this.facilityData.securityFee.enabled
        && this.facilityData.securityFee.fee > 0;
    },
    securityFeeTaxEnabled() {
      if (this.feeTaxEnabled) {
        return false;
      }
      if (this.facilityData.feeTypesTaxPricing && this.facilityData.feeTypesTaxPricing.length) {
        const securityFeeTax = this.facilityData.feeTypesTaxPricing.find(feeTypeTax => feeTypeTax.type === 'Security');
        if (securityFeeTax) {
          return true;
        }
      }
      return false;
    },
    feeTaxEnabled() {
      return this.facilityData.feeTaxFlag && this.facilityData.feeTypesTaxPricing && this.facilityData.feeTypesTaxPricing.length;
    },
    facilitySecurityFeeLabel() {
      let label = 'Security/CAM Fees';

      const facility = this.facilityData;
      if (facility && facility.securityFee && facility.securityFee.label) {
        label = facility.securityFee.label;
      }

      return label;
    },
    parentStage() {
      return ({
        start: this.lease.start,
        end: this.leaseEndDate,
        units: this.lease.units.map(unit => unit._id),
        status: 'CURRENT',
        step: 1,
        completed: false,
        active: true,
        lease: this.lease._id,
        facility: this.facility,
        customer: this.customer,
        selectedUnits: this.lease.units.map(unit => ({
          ...unit,
          editable: false,
          editPrice: 0,
          depositEditable: false,
          parentLeaseUnit: true,
          editDeposit: 0,
        })),
      });
    },
    summaryData() {
      return [this.parentStage, ...this.stages];
    },
    hasNoAgreement() {
      if (this.lease && !this.lease.agreement) {
        return true;
      }

      try {
        const agreement = JSON.parse(this.lease.agreement);
        return !Object.keys(agreement).length && !this.lease.agreementSigned;
      } catch (e) {
        return !this.lease.agreementSigned;
      }
    },
    hasACustomAgreement() {
      if (this.lease && !this.lease.agreement) {
        return false;
      }
      try {
        const agreement = JSON.parse(this.lease.agreement);
        return !Object.keys(agreement).length && (this.lease.agreementSigned && this.lease.agreementOnS3);
      } catch (e) {
        return this.lease.agreementSigned && this.lease.agreementOnS3;
      }
    },
    parentLeaseUnitIds() {
      return this.lease.units.map(unit => unit._id);
    },
  },
  data() {
    const transferDateValidator = (date) => {
      if (this.lease && new Date(this.lease.start) > new Date()) {
        const moveIn = new Date(
          new Date(this.lease.start).getTime(),
        );
        const moveOut = addDays(moveIn, 2);
        return date < moveOut;
      }

      // we need a date for actual move out
      const currentDate = new Date();
      return date < currentDate;
    };

    const disabledDate = (date) => {
      if (this.currentStep > 0) {
        if (this.stages[this.currentStep - 1].end) {
          const moveIn = new Date(this.stages[this.currentStep - 1].end);
          moveIn.setDate(moveIn.getDate() + 1);
          return date < moveIn;
        }
        const today = new Date();
        return date < today;
      }
      return transferDateValidator(date);

      // return true;
    };

    const moveOutdisabledDate = (date) => {
      if (this.stages[this.currentStep] && this.stages[this.currentStep].start) {
        const moveIn = new Date(this.stages[this.currentStep].start);
        moveIn.setDate(moveIn.getDate() + 1);
        return date < moveIn;
      }
      const today = new Date();
      return date < today;
    };

    return {
      loading: false,
      loadingUnits: false,
      facilityData: {},
      customerData: {},
      steps: [1],
      units: [],
      currentStep: 0,
      showSummary: false,
      activeLeaseUnits: [],
      leaseEndDate: null,
      sendAgreement: true, // Changed default to true - send agreements by default
      licensePayload: null,
      leaseTransfer: {
        useParentPricing: true,
        createdBy: this.$keycloak.tokenParsed.email,
        parent: this.lease._id,
        facility: this.facility,
        customer: this.customer,
        stages: 1,
        activeStage: 1,
      },
      stages: [],
      parentRentals: [],
      unitsSelected: [],
      availableUnitsSelected: [],
      transferDatePickerOptions: {
        disabledDate: transferDateValidator,
      },
      datePickerOptions: {
        disabledDate,
      },
      moveOutDatePickerOptions: {
        disabledDate: moveOutdisabledDate,
      },
    };
  },
  methods: {
    async getCustomer() {
      const response = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/${this.customer}`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });

      this.customerData = response.data;
    },
    stageLabel(stage) {
      if (!this.stages.length) {
        return 'Current';
      }
      if (stage === 1) {
        return 'Current';
      } else if (this.stages.length > 1 && stage === 2) {
        return 'Interim';
      }
      return 'Final';
    },
    stageLabelForm(stage) {
      if (stage === 1) {
        return 'Interim';
      }
      return 'Final';
    },
    labelForSelect(index) {
      const label = this.stageLabel(index + 1);

      return `Select Units for ${label} Stage`;
    },
    unitOptions(stageIndex) {
      const currentLeaseUnitIds = this.lease.units.map(u => u._id);

      return [{
        label: 'Current License Units',
        options: this.lease.units.map(unit => ({
          label: `${unit.name} - ${unit.computedSize} sq ft - Tier ${unit.tierQuality}`,
          value: unit._id,
          unitName: unit.name,
          size: unit.computedSize,
          tier: unit.tierQuality === 'AA' ? 'A+' : unit.tierQuality,
          price: this.computePriceForStep(unit, false, true, this.stages[stageIndex], true),
          disabled: false,
        })),
      }, {
        label: 'Available Units',
        options: this.stages[stageIndex].availableUnits ? this.stages[stageIndex].availableUnits.map(unit => ({
          label: `${unit.name} - ${unit.computedSize} sq ft - Tier ${unit.tierQuality}`,
          value: unit._id,
          unitName: unit.name,
          size: unit.computedSize,
          tier: unit.tierQuality === 'AA' ? 'A+' : unit.tierQuality,
          price: this.computePriceForStep(unit, false, true, this.stages[stageIndex]),
          disabled: currentLeaseUnitIds.includes(unit._id),
        })).filter(option => !option.disabled) : [],
      }];
    },

    // New method to determine tier tag type
    getTierTagType(tier) {
      switch (tier) {
        case 'AA':
        case 'A+':
          return 'success';
        case 'A':
          return 'primary';
        case 'B':
          return 'warning';
        case 'C':
          return 'info';
        default:
          return '';
      }
    },

    // New method to compute total current price
    computeTotalCurrentPrice() {
      return this.lease.units.reduce((total, unit) => {
        const parentRental = this.parentRentals.find(rental => rental.unit._id === unit._id);
        const price = parentRental && parentRental.updatedPrice
          ? parentRental.updatedPrice
          : parentRental.pricing.price || computeUnitPricing(unit, this.lease);
        return total + price;
      }, 0);
    },
    // New method to check if can proceed to review
    canProceedToReview() {
      // Check if all stages have required data
      return this.stages.every((stage, index) => {
        if (!stage.start) return false;
        if (stage.units.length === 0) return false;
        // If it's not the last stage and has an end date, next stage must be present
        if (stage.end && !this.stages[index + 1]) {
          return false;
        }
        // If it's not the last stage and has an end date, next stage must have a start date
        if (stage.end && index < this.stages.length - 1) {
          return this.stages[index + 1].start !== null;
        }
        return true;
      });
    },

    // New method to calculate stage total
    calculateStageTotal(stage) {
      return stage.selectedUnits.reduce((total, unit) => {
        const price = unit.editable
          ? unit.editPrice
          : this.computePriceForStep(unit, false, true, stage);
        return total + price;
      }, 0);
    },
    removeStage(index) {
      this.$confirm('Are you sure you want to remove this stage?', 'Confirmation', {
        confirmButtonText: 'Yes, Remove',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }).then(() => {
        this.stages.splice(index, 1);
        // Update step numbers for remaining stages
        this.stages.forEach((stage, idx) => {
          stage.step = idx + 2; // +2 because current lease is step 1
        });
        this.$message({
          type: 'success',
          message: 'Stage removed successfully',
        });

        // after removing stage, the last stage should be enabled.
        this.stages[this.stages.length - 1].disabled = false;
        this.stages[this.stages.length - 1].end = null;
      }).catch(() => {
        // User cancelled
      });
    },
    async setEndLeaseDate(date, index) {
      if (date && index === 0) {
        this.leaseEndDate = addDays(date, -1);
      }

      // this.stage[index].units = [];
      const units = await this.getUnits(index);
      this.stages[index].availableUnits = units.map(unit => ({
        ...unit,
        available: this.activeLeaseUnits.some(activeUnit => activeUnit._id === unit._id),
      }));
    },
    computeTax(price) {
      if (!this.facilityData.rentTaxFlag) {
        return 0;
      }
      const taxRate = this.facilityData.tax.rentTaxRate;

      const taxValue = price * (taxRate / 100);
      return taxValue;
    },
    allowAccess(role) {
      return allowAccess(role, this.$keycloak, this.$root.state.facility);
    },
    computeSecurityDeposit(unit, step, exception = false) {
      if (exception || this.leaseTransfer.useParentPricing) {
        const parentRental = this.parentRentals.find(rental => rental.unit._id === unit._id);
        if (parentRental) {
          return parentRental && parentRental.securityDeposit ? parentRental.securityDeposit : parentRental.depositFromLineItem;
        }
      }
      return this.computePriceForStep(unit, false, true, step);
    },
    computePriceForStep(unit, prorated, monthly, step, exception = false) {
      const totalMonths = getTotalMonths(
        new Date(step.start),
        step.end ? new Date(step.end) : null,
      );
      if (exception) {
        const parentRental = this.parentRentals.find(rental => rental.unit._id === unit._id);
        if (prorated && parentRental) {
          const price = parentRental && parentRental.updatedPrice
            ? parentRental.updatedPrice
            : parentRental.pricing.price;
          return this.computeProratedAmount(price, step.start, step.end);
        }
        if (!prorated && parentRental) {
          return parentRental && parentRental.updatedPrice
            ? parentRental.updatedPrice
            : parentRental.pricing.price;
        }
      }
      const leaseUnitIds = this.lease.units.map(u => u._id);
      const ifUnitIsFromExistingLease = this.leaseTransfer.useParentPricing
        && leaseUnitIds.includes(unit._id);
      if (ifUnitIsFromExistingLease && this.leaseTransfer.useParentPricing) {
        const parentRental = this.parentRentals.find(rental => rental.unit._id === unit._id);
        if (parentRental && prorated) {
          const price = parentRental.updatedPrice
            ? parentRental.updatedPrice
            : parentRental.pricing.price;
          return this.computeProratedAmount(price, step.start, step.end);
        }
        if (parentRental) {
          return parentRental.updatedPrice ? parentRental.updatedPrice : parentRental.pricing.price;
        }
      }
      const price = computeUnitPricing(
        unit,
        ifUnitIsFromExistingLease && this.leaseTransfer.useParentPricing
          ? this.lease
          : this.facilityData,
      );

      if (prorated) {
        return this.computeProratedAmount(price, step.start, step.end);
      }
      if (monthly) {
        return price;
      }
      return price * totalMonths;
    },
    computeProratedAmount(price, start, end, onlyMoveoutMonth = false) {
      const moveIn = new Date(start);
      const moveOut = end ? new Date(end) : null;
      const totalDaysOfMoveInMonth = this.getTotalDaysOfMoveInMonth(moveIn);

      if (!moveOut || this.datesBelongToSameMonth(moveIn, moveOut)) {
        const totalProrateDays = this.getTotalRemainingDays(moveIn, moveOut);

        if (totalProrateDays >= totalDaysOfMoveInMonth) {
          return price;
        }

        const oneDayPrice = price / totalDaysOfMoveInMonth;
        return totalProrateDays > 0 ? round(oneDayPrice * totalProrateDays) : 0;
      }

      const moveInProratedDays = (totalDaysOfMoveInMonth - moveIn.getDate()) + 1;
      if (!onlyMoveoutMonth) {
        return round((price / totalDaysOfMoveInMonth) * moveInProratedDays);
      }
      const totalDaysOfMoveOutMonth = this.getTotalDaysOfMoveInMonth(moveOut);
      const moveOutProratedDays = moveOut.getDate();

      return round(
        (price / totalDaysOfMoveOutMonth) * moveOutProratedDays
      );
    },
    datesBelongToSameMonth(start, end) {
      return start.getFullYear() === end.getFullYear()
        && start.getMonth() === end.getMonth();
    },
    getTotalDaysOfMoveInMonth(moveIn) {
      const currentYear = moveIn.getFullYear();
      const currentMonth = moveIn.getMonth();
      return new Date(currentYear, currentMonth + 1, 0).getDate();
    },
    async getFacility() {
      const facility = await axios.get(
        `${ADMIN_API_BASE}/facilities/${this.facility}`,
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );
      this.facilityData = facility.data;
    },
    async getAnvilPayload(eid) {
      try {
        const response = await axios.get(`${RS_ANVIL_API}/document/${eid}/details/payload`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

        return response.data;
      } catch (error) {
        console.error('Error fetching Anvil payload:', error);
        throw new Error('Failed to fetch Anvil payload');
      }
    },
    getTotalRemainingDays(start, end) {
      const moveIn = new Date(start);
      const moveOut = end ? new Date(end) : null;
      const date = moveIn.getDate();
      const currentYear = moveIn.getFullYear();
      const currentMonth = moveIn.getMonth();
      const totalDaysInCurrentMonth = new Date(
        currentYear,
        currentMonth + 1,
        0,
      ).getDate();

      if (moveOut) {
        if (this.datesBelongToSameMonth(moveIn, moveOut)) {
          const totalDays = moveOut.getDate() - date + 1;
          return totalDays > 0 ? totalDays : 1;
        }

        const moveInProratedDays = (totalDaysInCurrentMonth - date) + 1;
        const moveOutProratedDays = moveOut.getDate();
        return moveInProratedDays + moveOutProratedDays;
      }

      return totalDaysInCurrentMonth - date > 0
        ? (totalDaysInCurrentMonth - date) + 1
        : 1;
    },
    prepareSelectedUnit(summary) {
      for (const stage of this.stages) {
        if (stage.selectedUnits.length === stage.units.length && !summary) {
          continue;
        }

        if (summary) {
          // Validate before showing summary
          if (!this.canProceedToReview()) {
            this.$message.error('Please complete all required fields before reviewing');
            return;
          }
          this.showSummary = true;
          return;
        }
        // stage.selectedUnits = [];
        const previousSelectUnitsData = [...stage.selectedUnits];
        stage.selectedUnits = [];

        stage.units.forEach((unitId) => {
          const foundUnit = [...this.lease.units, ...this.units].find((unit) => {
            if (unit._id === unitId) {
              return unit;
            }
          });

          if (!foundUnit) return;

          const parentRental = this.parentRentals.find(rental => rental.unit._id === foundUnit._id);
          const unitAlreadySet = previousSelectUnitsData.find(unit => unit._id === unitId);

          if (unitAlreadySet) {
            stage.selectedUnits.push(unitAlreadySet);
            return;
          }
          const unitCopy = { ...foundUnit };
          const isCurrentLeaseUnit = this.parentLeaseUnitIds.includes(foundUnit._id);

          if (isCurrentLeaseUnit && this.leaseTransfer.useParentPricing && parentRental) {
            unitCopy.editable = false;
            unitCopy.depositEditable = false;
            unitCopy.validate = false;
            unitCopy.disabledEdit = true;
            unitCopy.editPrice = parentRental.updatedPrice || parentRental.pricing.price;
            unitCopy.editDeposit = parentRental.securityDeposit || parentRental.depositFromLineItem;
          } else {
            unitCopy.editable = false;
            unitCopy.editPrice = this.computePriceForStep(unitCopy, false, true, stage);
            unitCopy.depositEditable = false;
            unitCopy.editDeposit = this.computePriceForStep(unitCopy, false, true, stage);
            unitCopy.validate = false;
            unitCopy.disabledEdit = false;
          }

          stage.selectedUnits.push(unitCopy);
        });

        // Auto-show pricing when units are selected
        if (stage.units.length > 0) {
          stage.showPrice = true;
        }
      }
    },
    computeProratedSecurityFee(securityFee, start, end) {
      return this.computeProratedAmount(securityFee, start, end);
    },
    async validateStages() {
      this.stages.forEach((stage) => {
        if (!stage.start) {
          throw new Error(`Stage ${this.stageLabel(stage.step)} move in date cannot be empty`);
        }
        if (stage.step > 1 && stage.units.length === 0) {
          throw new Error('A stage which is not linked with current lease cannot be empty, please select units');
        }
      });
    },
    async prepareLineItems() {
      // add prorated price.
      this.loading = true;
      try {
        await this.validateStages();
      } catch (err) {
        this.$notify.error({
          message: err.message,
        });

        this.loading = false;
        return;
      }

      let licensePayload = null;
      let anvilTemplateId = null;
      if (!this.hasACustomAgreement && this.sendAgreement) {
        const agreement = JSON.parse(this.lease.agreement);
        const responsePayload = await this.getAnvilPayload(agreement.eid);
        const [fileName] = Object.keys(responsePayload);
        anvilTemplateId = this.facilityData.anvilTemplateId || ANVIL_TEMPLATE_ID2;
        if (fileName) {
          licensePayload = responsePayload[fileName].data;
          licensePayload.term = 'Month to Month';
          licensePayload.rsManagerName = this.$keycloak.tokenParsed.name;
          licensePayload.rsManagerTitle = getParsedTokenTitle(this.$keycloak);
          licensePayload.primaryContactName = this.customerData.primaryContact.name;
          licensePayload.primaryContactTitle = this.customerData.primaryContact.title;
          licensePayload.customerEmail = this.customerData.primaryContact.email || this.customerData.email;
        }
      }

      try {
        const securityFeeLineItemAppliedAt = this.securityFeeEnabled;
        const finalLineItems = [];
        const leaseUnitIds = this.lease.units.map(u => u._id);
        const stages = [this.parentStage, ...this.stages];
        stages.forEach((stage) => {
          const isParentStage = Boolean(stage.lease);
          const unitsInfo = stage.selectedUnits.map((unit) => {
            const parentRental = isParentStage && leaseUnitIds.includes(unit._id)
              ? this.parentRentals.find(rental => rental.unit._id === unit._id)
              : null;

            return ({
              customRent: isParentStage && parentRental
                ? Boolean(parentRental.updatedPrice)
                : unit.editable,
              customDeposit: isParentStage && parentRental
                ? Boolean(parentRental.appliedCustomDeposit)
                : unit.depositEditable,
              // eslint-disable-next-line no-nested-ternary
              rent: isParentStage && parentRental
                ? round(parentRental.updatedPrice || parentRental.pricing.price || this.computePriceForStep(unit, false, false, stage))
                : (unit.editable ? round(this.newPrice(unit, false, stage)) : round(this.computePriceForStep(unit, false, false, stage))),
              // eslint-disable-next-line no-nested-ternary
              deposit: isParentStage && parentRental
                ? round(parentRental.securityDeposit || parentRental.depositFromLineItem || this.computeSecurityDeposit(unit, false, true, stage))
                : (unit.depositEditable ? unit.editDeposit : this.computeSecurityDeposit(unit, false, true, stage)),
              name: unit.name,
              size: unit.computedSize,
              tier: unit.tierQuality,
              unit: unit._id,
            });
          });

          if (stage.lease) {
            stage.unitsInfo = unitsInfo;
            finalLineItems.push({
              stage,
              common: {
                ...this.leaseTransfer,
                transferStartDate: this.stages[0].start,
                parentInvoiceEndDate: setMoveoutToUTC(this.leaseEndDate),
                licensePayload,
                anvilTemplateId,
                sendAgreement: !this.hasACustomAgreement && !this.hasNoAgreement ? this.sendAgreement : false,
              },
            });
            return;
          }
          const lineItems = [];
          let accountBalance = Number(0);
          stage.selectedUnits.forEach((unit) => {
            const unitProratedPrice = unit.editable ? round(this.newPrice(unit, true, stage)) : round(this.computePriceForStep(unit, true, false, stage));
            const unitRentalTax = round(this.computeTax(unitProratedPrice));
            const securityDeposit = unit.depositEditable ? unit.editDeposit : this.computeSecurityDeposit(unit, false, true, stage);
            accountBalance += securityDeposit;

            lineItems.push({
              createdAt: new Date(),
              period: setMoveinToUTC(stage.start),
              description: `Security Deposit for unit ${unit.name}`,
              amount: round(securityDeposit),
              accountBalance: round(accountBalance),
              dueDate: setMoveinToUTC(stage.start),
              type: 'CREDIT',
              details: 'DEPOSIT',
              chargeType: 'DEPOSIT',
              unit: unit._id,
              source: uniqId,
            });
            accountBalance += unitProratedPrice;
            let uniqId = nanoid();
            lineItems.push({
              createdAt: new Date(),
              period: setMoveinToUTC(stage.start),
              description: `Prorated Rent from ${this.$options.filters.formatDate(stage.start)} of ${this.getTotalRemainingDays(stage.start, stage.end)} days for unit: ${
                unit.name
              }`,
              amount: unitProratedPrice,
              accountBalance,
              dueDate: setMoveinToUTC(stage.start),
              type: 'CREDIT',
              details: 'RENT',
              chargeType: 'RENT',
              unit: unit._id,
              uniqId,
            });

            if (unitRentalTax > 0 && !this.taxExempted) {
              accountBalance += unitRentalTax;
              let description = `Rental Tax for unit ${unit.name}`;
              if (this.facilityData && this.facilityData.tax && this.facilityData.tax.rentTaxLabel) {
                description = `${this.facilityData.tax.rentTaxLabel} @ ${this.facilityData.tax.rentTaxRate}% for unit ${unit.name} rent.`;
              }

              lineItems.push({
                createdAt: new Date(),
                period: setMoveinToUTC(stage.start),
                description,
                amount: round(unitRentalTax),
                accountBalance: round(accountBalance),
                dueDate: setMoveinToUTC(stage.start),
                type: 'CREDIT',
                details: 'TAX',
                chargeType: 'TAX',
                sourceType: 'RENT',
                unit: unit._id,
                source: uniqId,
              });
            }

            if (this.securityFeeEnabled) {
              uniqId = nanoid();
              const securityFee = this.computeSecurityFee(unit.computedSize);
              const proratedSecurityFee = this.computeProratedSecurityFee(securityFee, stage.start, stage.end);
              accountBalance += proratedSecurityFee;
              const securityFeeLineItem = {
                createdAt: new Date(),
                period: setMoveinToUTC(stage.start),
                description: ` Prorated ${this.facilitySecurityFeeLabel} from ${this.$options.filters.formatDate(stage.start)} of ${this.getTotalRemainingDays(stage.start, stage.end)} days for Unit ${unit.name}`,
                amount: proratedSecurityFee,
                accountBalance,
                dueDate: setMoveinToUTC(stage.start),
                details: 'FEES',
                chargeType: 'FEES',
                feeType: 'Security',
                unit: unit._id,
                type: 'CREDIT',
                uniqId,
              };
              lineItems.push(securityFeeLineItem);

              if (this.securityFeeTaxEnabled && !this.taxExempted) {
                this.computeSecurityFeeTax(proratedSecurityFee);
                const securityFeeTax = this.computeSecurityFeeTax(proratedSecurityFee);
                accountBalance += securityFeeTax;

                const securityFeeTaxLineItem = getLeaseTaxLineItem(securityFeeLineItem, this.facilityData);
                if (securityFeeTaxLineItem.amount) {
                  lineItems.push(securityFeeTaxLineItem);
                }
              }
            }
          });

          stage.unitsInfo = unitsInfo;
          const unitRentals = stage.selectedUnits.map(unit => ({
            createdBy: this.$keycloak.tokenParsed.email,
            moveIn: setMoveinToUTC(stage.start),
            moveOut: stage.end ? setMoveoutToUTC(stage.end) : null,
            status: 'PENDING',
            billingType: 'Monthly',
            facility: this.facility,
            ...(securityFeeLineItemAppliedAt ? { securityFeeAppliedAt: setMoveinToUTC(stage.start) } : {}),
            customer: this.customer,
            ...(unit.editable || this.leaseTransfer.useParentPricing ? {
              updatedPrice: unit.editPrice,
              updatedPriceAt: new Date(),
              updatedBy: this.$keycloak.tokenParsed.email,
            } : {}),
            unit: unit._id,
            ...(stillfullApplicableStatuses.includes(unit.status) ? {
              stillFullApplied: this.facilityData.stillFullFlag,
              stillFullPrice: this.facilityData.stillFullPrice,
            } : {}),
            lineItems: lineItems.filter(item => item.unit === unit._id),
            pricing: {
              price: this.computePriceForStep(unit, false, true, stage),
              dTier: this.leaseTransfer.useParentPricing && leaseUnitIds.includes(unit._id) ? this.lease.pricing.dTier : this.facilityData.pricing.dTier,
              qualityPricing: this.leaseTransfer.useParentPricing && leaseUnitIds.includes(unit._id) ? this.lease.pricing.qualityPricing : this.facilityData.pricing.qualityPricing,
              currency: this.facilityData.pricing.currency,
            },
            parkingPricing: {
              ...(this.leaseTransfer.useParentPricing && leaseUnitIds.includes(unit._id) ? this.lease.parkingPricing : this.facilityData.parkingPricing),
            },
            officePricing: {
              ...(this.leaseTransfer.useParentPricing && leaseUnitIds.includes(unit._id) ? this.lease.officePricing : this.facilityData.officePricing),
            },
            securityDeposit: unit.depositEditable ? unit.editDeposit : this.computeSecurityDeposit(unit, false, true, stage),
            appliedCustomDeposit: unit.depositEditable,
          }));

          finalLineItems.push({
            stage,
            lineItems,
            unitRentals,
            common: this.leaseTransfer,
            leasePayload: {
              createdBy: this.$keycloak.tokenParsed.email,
              facility: this.facility,
              customer: this.customer,
              unitRentals,
              customerInvoice: {
                start: setMoveinToUTC(stage.start),
                end: stage.end ? setMoveoutToUTC(stage.end) : null,
                units: stage.selectedUnits.map(unit => unit._id),
                status: 'PENDING',
                future: true,
                lineItems,
                facility: this.facility,
                customerId: this.customer,
                leaseStarted: false,
                amount: lineItems.reduce((acc, item) => acc + item.amount, 0),
                deposit: lineItems.filter(item => item.details === 'DEPOSIT').reduce((acc, item) => acc + item.amount, 0),
                amountPaid: 0,
                depositPaid: 0,
                pricing: {
                  price: lineItems.reduce((acc, item) => acc + item.amount, 0),
                  dTier: this.facilityData.pricing.dTier,
                  qualityPricing: this.facilityData.pricing.qualityPricing,
                  currency: this.facilityData.pricing.currency,
                },
                parkingPricing: {
                  ...this.facilityData.parkingPricing,
                },
                officePricing: {
                  ...this.facilityData.officePricing,
                },
                events: [{ type: 'booking_initiated', units: this.selectedUnits }],
              },
              chargeViaWallet: false,
              chargeViPG: false,
              amountToBePaidByWallet: 0,
              amountToBePaidByDefaultPayment: 0,
            },
          });
        });

        await axios.post(
          `${LEASE_TRANSFER_API}/v2/${this.facility}/${this.lease._id}`,
          {
            leaseTransferSteps: finalLineItems,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        this.$notify.success({
          message: 'Lease transfer is in progress.',
        });

        // Emit specific event for transfer creation
        this.$emit('completed', {
          status: 'creating',
          message: 'Transfer creation initiated successfully',
        });
      } catch (err) {
        this.$notify.error({
          message: err.response ? err.response.data : err.message,
        });
      }
      this.loading = false;
    },
    async addNewStageFromParent() {
      if (this.stages.length >= 3) {
        this.$message.warning('Maximum of 3 transfer stages allowed');
        return;
      }

      if (this.stages.length > 0) {
        // disable previous stage
        this.stages[this.stages.length - 1].disabled = true;
      }

      const currentStageSize = this.stages.length;
      this.stages.push({
        start: currentStageSize === 0 ? null : addDays(this.stages[this.stages.length - 1].end, 1),
        end: null,
        units: [],
        step: !this.stages.length ? 2 : this.stages.length + 2, // since current step is 0 and 1 + 1
        completed: false,
        active: false,
        selectedUnits: [],
        facility: this.facility,
        customer: this.customer,
        availableUnits: [],
        showPrice: false,
        disabled: false,
      });
      this.steps.push(this.stages.length);

      const units = await this.getUnits(currentStageSize);
      this.stages[currentStageSize].availableUnits = units.map(unit => ({
        ...unit,
        available: this.activeLeaseUnits.some(activeUnit => activeUnit._id === unit._id),
      }));

      // this.currentStep += 1;
    },
    onUnitSelected() {
      // this.getUnitRentals();
      this.prepareSelectedUnit();
    },
    newPrice(unit, prorated = false, stage) {
      return this.computeNewPrice(unit, prorated, stage);
    },
    computeNewPrice(unit, prorated, stage) {
      if (prorated) {
        return this.computeProratedAmount(unit.editPrice, stage.start, stage.end);
      }
      return round(unit.editPrice);
    },
    async getUnitRentals() {
      try {
        this.loading = true;
        const units = await axios.get(`${UNIT_RENTAL_API_BASE}/customer/${this.customer}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
          params: {
            limit: 250,
            ...(this.facility ? { facility: this.facility } : {}),
          },
        });
        this.loading = false;
        const leaseUnitIds = this.lease.units.map(u => u._id);
        this.parentRentals = units.data.result.filter(rental => leaseUnitIds.includes(rental.unit._id) && rental.unitInvoice === this.lease._id).map(item => ({ ...item, depositFromLineItem: item.lineItems.find(lineItem => lineItem.details === 'DEPOSIT').amount }));
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async getUnits(index) {
      const currentStep = index || this.currentStep;
      try {
        this.loadingUnits = true;
        const units = await axios.get(`${UNITS_API_BASE_V2}/${this.facility}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
          params: {
            limit: 250,
            status: 'AVAILABLE',
            ...(this.stages[currentStep] && this.stages[currentStep].start ? {
              moveIn: this.stages[currentStep].start,
            } : {}),
          },
        });
        this.units = [...units.data.result, ...units.data.availableUnits];
        this.units = this.units.sort((a, b) =>
          a.name.localeCompare(b.name, 'en', { numeric: true }),
        );
        this.loadingUnits = false;
        return this.units;
      } catch (err) {
        this.loadingUnits = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
        return [];
      }
    },
    computeSecurityFee(size) {
      if (this.securityFeeEnabled) {
        return round((this.facilityData.securityFee.fee / 100) * size);
      }
      return 0;
    },
    computeSecurityFeeTax(fee) {
      if (!this.securityFeeTaxEnabled) {
        return 0;
      }
      const taxRate = this.facilityData.tax.otherTaxRate;
      return round(fee * (taxRate / 100));
    },
  },
};
</script>

<style scoped>
/* Header Styles */
.transfer-header {
  text-align: center;
  margin-bottom: 30px;
  padding: 20px 0;
}

/* Timeline Styles */
.timeline-container {
  margin: 40px 0;
  padding: 0 50px;
}

.timeline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.timeline-dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e4e7ed;
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 10px;
  transition: all 0.3s;
}

.timeline-item.active .timeline-dot {
  background-color: #409eff;
  color: white;
}

.timeline-item.completed .timeline-dot {
  background-color: #67c23a;
  color: white;
}

.timeline-label {
  font-size: 14px;
  color: #606266;
  text-align: center;
  max-width: 120px;
}

.timeline-line {
  flex: 1;
  height: 2px;
  background-color: #e4e7ed;
  margin: 0 10px;
  transform: translateY(-20px);
}

/* Card Styles */
.current-license-card {
  margin-bottom: 20px;
  background-color: #f5f7fa;
}

.card-header-custom {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
}

.card-header-custom i {
  margin-right: 10px;
  font-size: 20px;
  color: #409eff;
}

.info-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}

.info-item label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.info-item span {
  font-size: 16px;
  color: #303133;
  font-weight: 500;
}

.maintain-pricing-option {
  text-align: center;
  padding: 10px 0;
}

/* Stage Card Styles */
.stages-container {
  margin-bottom: 30px;
}

.stage-card {
  margin-bottom: 20px;
  border-left: 4px solid #58bbda;
}

.stage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stage-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
}

.stage-title i {
  margin-right: 10px;
  color: #58bbda;
}

.stage-title .el-tag {
  margin-left: 10px;
}

.remove-stage-btn {
  color: #f56c6c;
}

.stage-info-alert {
  margin-bottom: 20px;
}

/* Form Styles */
.form-help-text {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

/* Unit Selection Styles */
.unit-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 5px 0;
}

.unit-name {
  font-weight: 500;
  color: #303133;
}

.unit-details {
  color: #606266;
  font-size: 14px;
  margin: 0 10px;
}

.unit-price {
  color: #409eff;
  font-weight: 500;
  margin-left: auto;
}

/* Pricing Section Styles */
.pricing-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.pricing-title {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #303133;
  display: flex;
  align-items: center;
}

.pricing-title i {
  margin-right: 8px;
  color: #67c23a;
}

.pricing-table {
  border-radius: 4px;
  overflow: hidden;
}

.price-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.price-display {
  font-weight: 500;
  color: #303133;
}

.fee-display {
  color: #606266;
}

/* Action Buttons */
.action-buttons {
  text-align: center;
  padding: 30px 0;
  border-top: 1px solid #ebeef5;
  margin-top: 30px;
}

.action-buttons .el-button {
  margin: 0 10px;
}

/* Summary View Styles */
.summary-card {
  background-color: #fafafa;
}

.summary-header {
  text-align: center;
  margin-bottom: 30px;
}

.summary-title {
  font-size: 24px;
  color: #303133;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-title i {
  margin-right: 10px;
  color: #67c23a;
  font-size: 28px;
}

.summary-subtitle {
  font-size: 14px;
  color: #606266;
  margin: 0;
}

.timeline-summary {
  margin-bottom: 30px;
}

.summary-stage {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
}

.stage-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.stage-dates {
  color: #606266;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.stage-dates i {
  margin: 0 5px;
  font-size: 12px;
}

.summary-table {
  margin-bottom: 15px;
}

.summary-price {
  font-weight: 500;
  color: #303133;
}

.stage-summary-total {
  text-align: right;
  padding-top: 15px;
  border-top: 1px solid #ebeef5;
  font-size: 16px;
}

.stage-summary-total span {
  color: #606266;
  margin-right: 10px;
}

.stage-summary-total strong {
  color: #409eff;
  font-size: 18px;
}

/* Agreement Section */
.agreement-section {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.agreement-section h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 16px;
}

.agreement-switch {
  margin-top: 15px;
}

.summary-actions {
  text-align: center;
  padding-top: 20px;
}

.summary-actions .el-button {
  margin: 0 10px;
}

/* Responsive adjustments */
@media (max-width: 568px) {
  .timeline-container {
    padding: 0 20px;
  }

  .timeline-label {
    font-size: 12px;
    max-width: 80px;
  }

  .unit-option {
    flex-direction: column;
    align-items: flex-start;
  }

  .unit-price {
    margin-left: 0;
    margin-top: 5px;
  }
}

/* Loading states */
.el-select[loading] .el-select-dropdown {
  opacity: 0.5;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// src/components/UnitLeaseTransfer.vue